import { Modal, ModalBody, ModalFoot, ModalHead } from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { User } from 'lib-shared-types-api';
import { FormEvent } from 'react';

import { AgentsSelect } from 'fable/agentsSelect';
import { withSelection, WithSelectionInjectedProps } from 'hocs';
import { useNotifications } from 'hooks/useNotifications';
import { getUserDisplayName } from 'utils/user';

import styles from './agentsAddModal.module.scss';

export interface AgentsAddModalProps extends WithSelectionInjectedProps<User> {
  isOpen: boolean;
  onClose: (canceled?: boolean) => void;
  addAgents: (agentIds: string[]) => Promise<unknown>;
  parentEntity: 'team' | 'queue' | 'role';
  teamId?: string;
  queueId?: string;
}

const AgentsAddModalImpl = (props: AgentsAddModalProps): JSX.Element => {
  const notify = useNotifications();

  const handleSubmit = (e?: FormEvent) => {
    e?.preventDefault();

    const {
      selection: { selectedItems },
      addAgents,
      parentEntity,
    } = props;

    ampli.addAgentsModalFormSubmitted({
      addTo: parentEntity,
      selectedAgents: selectedItems.map((item) => ({
        id: item.id,
        name: getUserDisplayName(item),
      })),
    });

    if (!selectedItems.length) {
      return;
    }

    addAgents(selectedItems.map((value) => value.id))
      .then(() => {
        const notificationOptions = {
          title: `Agent(s) added to ${parentEntity}`,
          text: `${selectedItems.length} agent(s) has been added to the ${parentEntity}.`,
          icon: 'i-done',
          className: 'success',
          time: 5000,
        };

        notify(notificationOptions);
        handleClose();
      })
      .catch((e) => {
        const notificationOptions = {
          title: 'An error occurred',
          text: `An error occurred when trying to add agent(s) to the ${parentEntity}.`,
          icon: 'i-exclamation',
          className: 'danger',
        };

        notify(notificationOptions);
        throw new Error(`Failed to add agent(s) to ${parentEntity}`);
      });
  };

  const handleClose = (canceled = false) => {
    const { selection, onClose } = props;

    selection.setSelectedItems([]);
    onClose(canceled);
  };

  const { isOpen, selection, parentEntity, teamId, queueId } = props;

  return (
    <Modal isOpen={isOpen} onClose={() => handleClose(true)}>
      <ModalHead
        title={`Add agent(s) to ${parentEntity}`}
        onClose={() => handleClose(true)}
      />
      <form onSubmit={handleSubmit}>
        <ModalBody className={styles.addAgentsModalBody}>
          <AgentsSelect
            selectedItems={selection.selectedItems}
            selectAgent={selection.selectItem}
            deselectAgent={selection.deselectItem}
            teamId={teamId}
            queueId={queueId}
          />
        </ModalBody>
        <ModalFoot
          cancelOnClick={() => handleClose(true)}
          cancelName="Cancel"
          confirmOnClick={handleSubmit}
          confirmName="Add agent(s)"
          confirmProps={{
            disabled: !selection.selectedItems.length,
          }}
        />
      </form>
    </Modal>
  );
};

export const AgentsAddModal = withSelection<AgentsAddModalProps, User>(
  AgentsAddModalImpl,
);
